import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CardConfiguration } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { formatDateOnly, parseDateForDisplay } from 'src/app/global/utils/date/date.utils';
import { languageSelector, organisationConfigurationSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { ResidenceService } from 'src/app/residence/service/residence.abstract.service';
import { ProfileAddress, getResidenceDisplayState, needsReview as residenceNeedsReview } from 'src/app/residence/residence.model';
import { CardSelectorFE, UserRecord, CardAction, CardStatus, CardType, UserCardFE } from '../../user.model';
import { UserService } from '../../service/user.abstract.service';

@Component({
    selector: 'app-update-card-dialog',
    templateUrl: './update-card-dialog.component.html',
    styleUrls: ['./update-card-dialog.component.scss'],
})
export class UpdateCardDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();
    cardLabel: string;
    cardIcon: string;
    cardTypes: any[] = [];

    hideExpDate = false;

    public stepIndex = 0;
    public steps: string[] = [];

    // Address selector
    userAddresses: ProfileAddress[] = [];
    selectedAddressIndex = 0;
    subTypeLocked = false;

    public form: any = new FormGroup({
        firstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        lastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        picture: new FormControl('', [Validators.required]),
        birthDate: new FormControl('', [Validators.required, this.validateDateIsInPast]),
        address: new FormControl('', [Validators.required]),
        expDate: new FormControl('', [Validators.required, this.validateDateIsInFutur]),
        subType: new FormControl(''),
        sex: new FormControl('', [Validators.required]),
        cardTypes: new FormArray([]),
    });

    cardConfig: any;
    cardConfigs: CardConfiguration[];
    instructionsStepsInfo: any;
    instructionsStepsConfirmation: any;
    subTypes: any[] = [];
    processingSubmit = false;
    tomorrow = new Date();
    maxBirthDate = new Date();

    loading = true;

    orgTag: string;

    get sexOptions() {
        const options = [
            { id: 1, value: 'female', label: `${this.translateService.instant('pages.user-cards.create.steps.information.female')}` },
            { id: 2, value: 'male', label: `${this.translateService.instant('pages.user-cards.create.steps.information.male')}` },
            { id: 3, value: 'other', label: `${this.translateService.instant('pages.user-cards.create.steps.information.other')}` },
        ];

        return this.orgTag === 'BROSSARD' ? options.filter((option) => option.value !== 'other') : options;
    }

    constructor(
        private translateService: TranslateService,
        private dialogRef: MatDialogRef<UpdateCardDialogComponent>,
        private formBuilder: FormBuilder,
        private userService: UserService,
        private messageService: MessageService,
        private store: Store<State>,
        private cdr: ChangeDetectorRef,
        private residenceService: ResidenceService,
        @Inject(MAT_DIALOG_DATA) public data: { card: Partial<UserCardFE>; action: CardAction; userRecord: UserRecord; selectableCards: CardSelectorFE[] },
    ) {}

    get firstName(): AbstractControl {
        return this.form.get('firstName');
    }

    get lastName(): AbstractControl {
        return this.form.get('lastName');
    }

    get birthDate(): AbstractControl {
        return this.form.get('birthDate');
    }

    get sex(): AbstractControl {
        return this.form.get('sex');
    }

    get address(): AbstractControl {
        return this.form.get('address');
    }

    get expDate(): AbstractControl {
        return this.form.get('expDate');
    }

    get subType(): AbstractControl {
        return this.form.get('subType');
    }

    get picture(): AbstractControl {
        return this.form.get('picture');
    }

    get cardType(): CardType {
        return this.data.card.cardType;
    }

    public validateDateIsInPast(c: FormControl): { [key: string]: { [key: string]: boolean } } {
        if (c.value) {
            const today = new Date().toISOString();
            return new Date(c.value).toISOString() < today ? null : { validateDateIsInPast: { valid: false } };
        }
        return null;
    }

    public validateDateIsInFutur(c: FormControl): { [key: string]: { [key: string]: boolean } } {
        if (c.value) {
            const today = new Date().toISOString();
            return new Date(c.value).toISOString() > today ? null : { validateDateIsInFutur: { valid: false } };
        }
        return null;
    }

    ngOnInit() {
        this.loading = true;
        this.tomorrow.setDate(this.tomorrow.getDate() + 1);
        combineLatest([this.store.select(languageSelector), this.store.select(organisationConfigurationSelector), this.store.select(organisationTagSelector)])
            .pipe(
                takeUntil(this.destroy$),
                filter(([lang, orgConfig]) => !!orgConfig),
                map(([lang, orgConfig, orgTag]) => {
                    this.orgTag = orgTag;
                    this.initForm();
                    (this.translateService.instant('pages.user-cards.create.steps.information.heading'), this.translateService.instant('pages.user-cards.create.steps.confirmation.heading'));

                    this.cardConfigs = orgConfig?.support?.cards;
                    this.cardConfig = orgConfig?.support?.cards?.find((c) => c.type === this.data.card.cardType);
                    this.instructionsStepsInfo = this.cardConfig?.identity?.instructions?.steps?.info?.[lang] || '';
                    this.instructionsStepsConfirmation = this.cardConfig?.identity?.instructions?.steps?.confirmation?.[lang] || '';
                    if (this.cardConfig?.availableSubTypes?.length > 0) {
                        const subTypes = [];
                        this.cardConfig?.availableSubTypes.forEach((availableSubType) => {
                            const subType = orgConfig?.subTypes?.find((s) => s.tag === availableSubType);
                            subTypes.push({ label: subType?.name?.[lang], value: subType.tag });
                        });
                        this.subTypes = subTypes;
                        this.subType.setValidators([Validators.required]);
                    }
                    this.cardLabel = this.cardConfig?.identity?.label[lang];
                    this.cardIcon = this.cardConfig?.identity?.icon || 'icon-my-card';
                    this.initCardForm();
                    this.loadUserAddresses();
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    private initCardForm() {
        this.form.controls.firstName.setValue(this.data.card.firstName);
        this.form.controls.lastName.setValue(this.data.card.lastName);
        this.form.controls.address.setValue(this.data.card.address);
        this.form.controls.subType.setValue(this.subTypes?.find((subType) => subType.value == this.data.card.subType));
        this.form.controls.picture.setValue(this.data.card.photo);

        if (this.data.card.sex) {
            this.form.controls.sex.setValue(this.sexOptions.find((option) => option.value === this.data.card.sex) || this.sexOptions[0]);
        } else {
            this.form.controls.sex.setValue(this.sexOptions.find((option) => option.value === this.data.userRecord.sex) || this.sexOptions[0]);
        }

        if (this.data.card.birthDate) {
            this.birthDate.setValue(parseDateForDisplay(this.data.card.birthDate) || '');
        } else {
            this.birthDate.setValue(parseDateForDisplay(this.data.userRecord.birthDate) || '');
        }

        if (this.data.card.expirationDate) {
            this.expDate.setValue(parseDateForDisplay(this.data.card.expirationDate) || '');
        } else {
            this.expDate.setValue('');
        }

        this.addCardTypesControls();
    }

    private loadUserAddresses(): void {
        const addresses = this.data.userRecord.addresses || [];
        const userId = this.data.userRecord.id;
        this.residenceService
            .getCitizenResidences(userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((residences) => {
                const matched = new Set<string>();
                this.userAddresses = addresses.map((addr) => {
                    const addrStreet = (addr.streetName || addr.street || '').replace(/\s+/g, '').toLowerCase();
                    const addrPostCode = (addr.postCode || '').replace(/\s+/g, '').toLowerCase();
                    const addrNumber = String(addr.number || '');
                    const matchingResidences = residences.filter(
                        (r) =>
                            !matched.has(r._id) &&
                            (r.address.streetName || r.address.street || '').replace(/\s+/g, '').toLowerCase() === addrStreet &&
                            String(r.address.number || '') === addrNumber &&
                            (r.address.postCode || '').replace(/\s+/g, '').toLowerCase() === addrPostCode,
                    );
                    matchingResidences.forEach((r) => matched.add(r._id));
                    const orgLinks = matchingResidences.map((r) => ({
                        tag: r.organisation,
                        name: r.organisation,
                        isPrimary: r.isPrimary,
                        verificationStatus: r.verification.status,
                        residenceId: r._id,
                        displayState: getResidenceDisplayState(r),
                        needsReview: residenceNeedsReview(r),
                        expirationDate: r.expirationDate,
                        subType: r.subType,
                    }));
                    return {
                        address: addr,
                        displayName: matchingResidences[0]?.name ?? (addr as any).name ?? `${addr.streetName}, ${addr.city}`,
                        orgLinks,
                        isCasual: orgLinks.length === 0,
                        isVerified: orgLinks.some((link) => link.verificationStatus === 'APPROVED'),
                    };
                });
                this.preselectAddress();
                this.syncSubTypeFromAddress(this.userAddresses[this.selectedAddressIndex]);
            });
    }

    private preselectAddress(): void {
        const currentAddr = this.form.get('address').value;
        if (!currentAddr || this.userAddresses.length === 0) return;
        const idx = this.userAddresses.findIndex((pa) => {
            const a = pa.address;
            return (
                (a.streetName || a.street || '').replace(/\s+/g, '').toLowerCase() === (currentAddr.streetName || currentAddr.street || '').replace(/\s+/g, '').toLowerCase() &&
                String(a.number || '') === String(currentAddr.number || '') &&
                (a.postCode || '').replace(/\s+/g, '').toLowerCase() === (currentAddr.postCode || '').replace(/\s+/g, '').toLowerCase()
            );
        });
        if (idx >= 0) this.selectedAddressIndex = idx;
    }

    onAddressSelected(index: number): void {
        this.selectedAddressIndex = index;
        const pa = this.userAddresses[index];
        if (pa?.address) {
            this.form.patchValue({ address: pa.address });
        }
        this.syncSubTypeFromAddress(pa);
    }

    private syncSubTypeFromAddress(pa: ProfileAddress): void {
        if (!pa || !this.subTypes?.length) return;

        const residenceSubType = pa.orgLinks?.[0]?.subType;
        if (residenceSubType) {
            const match = this.subTypes.find((st) => st.value === residenceSubType);
            if (match) {
                this.form.controls.subType.setValue(match);
            }
            this.subTypeLocked = true;
        } else {
            this.subTypeLocked = false;
        }
    }

    public sexChanged(option) {
        this.form.controls.sex.setValue(option.value);
    }

    public cardTypeChanged(option) {
        this.form.controls.cardType.setValue(option.value);
    }

    public initForm() {
        if (this.data.card.cardType === CardType.LEGACY) {
            this.hideExpDate = true;
            this.form.controls.expDate.clearValidators();
            this.form.controls.expDate.updateValueAndValidity();
            this.form.updateValueAndValidity();
        }
        this.form.patchValue({ cardType: this.data.card.cardType, expDate: this.data.card.expirationDate });
    }

    public closeDialog() {
        this.dialogRef.close();
    }

    public next() {
        this.stepIndex += 1;
        this.cdr.detectChanges();
    }

    public back() {
        this.stepIndex -= 1;
        this.cdr.detectChanges();
    }

    public submit() {
        if (this.form.valid) {
            this.processingSubmit = true;

            const payload: any = {
                expDate: formatDateOnly(this.expDate?.value) || null,
                birthDate: formatDateOnly(this.birthDate.value),
                firstName: this.form.get('firstName').value,
                lastName: this.form.get('lastName').value,
                photo: this.form.get('picture').value,
                address: this.form.get('address').value,
                subType: this.form.get('subType').value.value,
                sex: this.form.get('sex').value.value,
                cardTypes: this.form.value.cardTypes.map((v, i) => (v ? this.cardTypes[i].cardType : null)).filter((v) => v !== null),
            };
            this.submitUpdate(payload);
        }
    }

    public submitUpdate(payload: any) {
        this.userService
            .patchUpdateCard(payload, this.data.card._id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.dialogRef.close({ connectedCardUpdated: this.isConnectorCard() });
                    this.processingSubmit = false;
                    this.displaySuccessMessage();
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translateService.instant('pages.user-cards.update.messages.error'));
                },
            );
    }

    private addCardTypesControls() {
        // Excludes cards with a renewal in progress: editing is blocked for them (see card.component.ts's
        // updateCardExpDate guard) since the backend rejects edits while card.renewal is set — applying
        // changes to them here would bypass that same protection.
        const selectableCards = this.data.selectableCards.filter(
            (selectableCard) => (selectableCard.status === CardStatus.ACTIVE || selectableCard.status === CardStatus.EXPIRED) && !selectableCard.renewal,
        );

        this.cardTypes = selectableCards;
        selectableCards.map((selectableCard, i) => {
            const control = new FormControl(selectableCard.cardType === this.data.card.cardType);
            (this.form.controls.cardTypes as FormArray).push(control);
        });
    }

    public isConnectorCard() {
        switch (this.data.card.cardType) {
            case CardType.BCITI:
            case CardType.CITIZEN:
                return false;
            case CardType.LEGACY:
            case CardType.LIBRARY:
            case CardType.SPORTS:
                return true;
            default:
                console.error(`unknown cardType ${this.data.card.cardType}`);
                break;
        }
    }

    public displaySuccessMessage() {
        switch (this.data.card.cardType) {
            case CardType.BCITI:
            case CardType.CITIZEN:
                this.messageService.success(this.translateService.instant('pages.user-cards.update.messages.successNoConnector'));
                break;
            case CardType.LEGACY:
            case CardType.LIBRARY:
            case CardType.SPORTS:
                this.messageService.success(this.translateService.instant('pages.user-cards.update.messages.successConnector'));
                break;
            default:
                console.error(`unknown cardType ${this.data.card.cardType}`);
                break;
        }
    }

    getCardButtonIcon(cardType: CardType) {
        const cardConfig = this.cardConfigs?.find((c) => c.type === cardType);
        switch (cardType) {
            case CardType.BCITI:
            case CardType.LEGACY:
            case CardType.CITIZEN:
            case CardType.LIBRARY:
            case CardType.SPORTS:
                return cardConfig.identity?.icon;
            default:
                return 'icon-my-card';
        }
    }

    trackByCardType(index: number, item: any): string {
        return item.cardType; // Unique identifier (you could also use item.id if available)
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
